import React, { useState } from 'react';
import { 
  Rocket, AlertTriangle, Shield, CheckCircle2, ExternalLink,
  Mail, Bell, Star, Zap, Globe, DollarSign, Users, Award
} from 'lucide-react';

// Tax Software Comparison Data
const taxSoftwareData = {
  usa: [
    {
      name: 'CoinTracker',
      logo: 'C',
      color: 'blue',
      countries: 'USA, UK, Canada, Australia',
      pricing: 'Free - $199/yr',
      bestFor: 'IRS Form 8949 auto-generation',
      features: ['IRS compliant', 'Exchange sync', 'Tax-loss harvesting'],
      link: 'https://www.binance.com/register?ref=1229829685' // Affiliate placeholder
    },
    {
      name: 'Koinly',
      logo: 'K',
      color: 'purple',
      countries: 'USA + 20 countries',
      pricing: '$49 - $279/yr',
      bestFor: 'Multi-exchange portfolio tracking',
      features: ['Auto-import', 'DeFi support', 'NFT tracking'],
      link: 'https://www.binance.com/register?ref=1229829685'
    },
    {
      name: 'TaxBit',
      logo: 'T',
      color: 'green',
      countries: 'USA, UK, Canada',
      pricing: 'Free basic plan',
      bestFor: 'Enterprise-grade accuracy',
      features: ['IRS partner', 'Audit defense', 'CPA support'],
      link: 'https://www.binance.com/register?ref=1229829685'
    }
  ],
  uk: [
    {
      name: 'Koinly',
      logo: 'K',
      color: 'purple',
      countries: 'UK + 20 countries',
      pricing: '£49 - £279/yr',
      bestFor: 'HMRC Capital Gains reports',
      features: ['HMRC format', 'SA100 ready', 'DeFi support'],
      link: 'https://www.binance.com/register?ref=1229829685'
    },
    {
      name: 'CoinTracker',
      logo: 'C',
      color: 'blue',
      countries: 'UK, USA, Canada, Australia',
      pricing: 'Free - £159/yr',
      bestFor: 'Multi-asset portfolio tracking',
      features: ['HMRC compliant', 'Auto-sync', 'Tax-loss harvesting'],
      link: 'https://www.binance.com/register?ref=1229829685'
    },
    {
      name: 'Recap',
      logo: 'R',
      color: 'teal',
      countries: 'UK focused',
      pricing: '£149/yr',
      bestFor: 'UK-specific tax rules',
      features: ['Built for UK', 'Same-day matching', 'Section 104'],
      link: 'https://www.binance.com/register?ref=1229829685'
    }
  ],
  europe: [
    {
      name: 'Koinly',
      logo: 'K',
      color: 'purple',
      countries: 'Germany, France, Italy + 20',
      pricing: '€49 - €279/yr',
      bestFor: 'Multi-country EU support',
      features: ['German tax reports', 'FIFO/LIFO', 'DeFi tracking'],
      link: 'https://www.binance.com/register?ref=1229829685'
    },
    {
      name: 'Blockpit',
      logo: 'B',
      color: 'orange',
      countries: 'Germany, Austria, Switzerland',
      pricing: '€49 - €199/yr',
      bestFor: 'DACH region specialists',
      features: ['German compliant', '1-year rule', 'Steuererklärung'],
      link: 'https://www.binance.com/register?ref=1229829685'
    },
    {
      name: 'CoinTracking',
      logo: 'CT',
      color: 'amber',
      countries: 'Germany + EU wide',
      pricing: 'Free - €199/yr',
      bestFor: 'Advanced traders & tax pros',
      features: ['Tax reports', '13 methods', 'API import'],
      link: 'https://www.binance.com/register?ref=1229829685'
    }
  ],
  bitcoin: [
    {
      name: 'CoinTracker',
      logo: 'C',
      color: 'blue',
      countries: 'USA, UK, Canada, Australia',
      pricing: 'Free - $199/yr',
      bestFor: 'Bitcoin FIFO tracking',
      features: ['BTC cost basis', 'UTXO tracking', 'Tax-loss harvesting'],
      link: 'https://www.binance.com/register?ref=1229829685'
    },
    {
      name: 'Bitcoin.tax',
      logo: 'BT',
      color: 'orange',
      countries: 'USA, Canada, UK, Australia',
      pricing: '$39 - $199/yr',
      bestFor: 'Bitcoin-focused calculations',
      features: ['BTC specialist', 'LIFO/FIFO/HIFO', 'Form 8949'],
      link: 'https://www.binance.com/register?ref=1229829685'
    },
    {
      name: 'Koinly',
      logo: 'K',
      color: 'purple',
      countries: 'Global',
      pricing: '$49 - $279/yr',
      bestFor: 'Multi-asset including BTC',
      features: ['All exchanges', 'Wallet sync', 'DeFi + NFT'],
      link: 'https://www.binance.com/register?ref=1229829685'
    }
  ]
};

const colorMap = {
  blue: { bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400' },
  purple: { bg: 'bg-purple-500/20', border: 'border-purple-500/30', text: 'text-purple-400' },
  green: { bg: 'bg-green-500/20', border: 'border-green-500/30', text: 'text-green-400' },
  orange: { bg: 'bg-orange-500/20', border: 'border-orange-500/30', text: 'text-orange-400' },
  amber: { bg: 'bg-amber-500/20', border: 'border-amber-500/30', text: 'text-amber-400' },
  teal: { bg: 'bg-teal-500/20', border: 'border-teal-500/30', text: 'text-teal-400' }
};

// CTA Block Component
export const TaxOptimizerCTA = ({ taxOwed = null, region = 'usa', complianceText = '' }) => {
  const formattedTax = taxOwed !== null ? Math.abs(taxOwed).toLocaleString('en-US', {
    style: 'currency',
    currency: region === 'uk' ? 'GBP' : region === 'europe' ? 'EUR' : 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }) : null;

  return (
    <div className="mt-6 p-5 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/30">
      <div className="flex items-center gap-2 mb-3">
        <Rocket size={20} className="text-green-400" />
        <h3 className="text-lg font-bold text-white">Optimize Your Crypto Taxes</h3>
      </div>
      
      {formattedTax && (
        <p className="text-xl font-semibold text-amber-400 mb-2">
          You may owe {formattedTax} in crypto taxes
        </p>
      )}
      
      <p className="text-sm text-gray-400 mb-3">
        Manual calculations can lead to costly mistakes and penalties.
      </p>
      
      {complianceText && (
        <p className="text-sm text-green-400 mb-4 flex items-center gap-2">
          <Shield size={14} />
          {complianceText}
        </p>
      )}
      
      <a
        href="https://www.binance.com/register?ref=1229829685"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500/20 hover:bg-green-500/30 border border-green-500/40 hover:border-green-500/60 rounded-lg text-green-400 font-medium transition-all"
      >
        Use Automated Crypto Tax Software
        <ExternalLink size={16} />
      </a>
    </div>
  );
};

// Comparison Table Component
export const TaxSoftwareTable = ({ region = 'usa' }) => {
  const software = taxSoftwareData[region] || taxSoftwareData.usa;

  return (
    <div className="mt-8 mb-8">
      <div className="flex items-center gap-2 mb-4">
        <Award size={20} className="text-amber-400" />
        <h3 className="text-xl font-bold text-white">Best Crypto Tax Software (2026)</h3>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left p-3 text-sm font-semibold text-gray-400">Tool</th>
              <th className="text-left p-3 text-sm font-semibold text-gray-400">Countries</th>
              <th className="text-left p-3 text-sm font-semibold text-gray-400">Pricing</th>
              <th className="text-left p-3 text-sm font-semibold text-gray-400">Best For</th>
              <th className="text-center p-3 text-sm font-semibold text-gray-400"></th>
            </tr>
          </thead>
          <tbody>
            {software.map((tool, index) => {
              const colors = colorMap[tool.color] || colorMap.blue;
              return (
                <tr 
                  key={tool.name}
                  className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
                >
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${colors.bg} ${colors.border} border flex items-center justify-center`}>
                        <span className={`font-bold ${colors.text}`}>{tool.logo}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-white">{tool.name}</p>
                        <div className="flex gap-1 mt-1">
                          {[1, 2, 3, 4, 5].map(i => (
                            <Star key={i} size={10} className={i <= 4 ? 'text-amber-400 fill-amber-400' : 'text-gray-600'} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1.5 text-sm text-gray-400">
                      <Globe size={12} />
                      {tool.countries}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-green-400 font-medium">{tool.pricing}</span>
                  </td>
                  <td className="p-3">
                    <p className="text-sm text-gray-300">{tool.bestFor}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {tool.features.slice(0, 2).map(f => (
                        <span key={f} className="text-xs px-1.5 py-0.5 bg-gray-800 rounded text-gray-400">
                          {f}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <a
                      href={tool.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-1 px-3 py-1.5 ${colors.bg} ${colors.border} border rounded-lg ${colors.text} text-sm font-medium hover:opacity-80 transition-opacity`}
                    >
                      Try Free
                      <ExternalLink size={12} />
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Trust & Urgency Block Component
export const TrustUrgencyBlock = ({ region = 'usa' }) => {
  const regionTexts = {
    usa: {
      penalty: 'IRS penalties for crypto tax errors can reach 75% of underpaid taxes.',
      compliance: 'Ensure IRS-compliant Form 8949 reporting'
    },
    uk: {
      penalty: 'HMRC penalties for incorrect CGT returns can be up to 100% of the tax owed.',
      compliance: 'Stay compliant with HMRC Capital Gains Tax regulations'
    },
    europe: {
      penalty: 'EU tax authorities are increasing crypto audits under DAC8 reporting rules.',
      compliance: 'Meet your country-specific crypto reporting requirements'
    },
    bitcoin: {
      penalty: 'Tax authorities are cracking down on unreported Bitcoin gains.',
      compliance: 'Report all BTC transactions accurately to avoid penalties'
    }
  };

  const text = regionTexts[region] || regionTexts.usa;

  return (
    <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-red-500/5 border border-amber-500/30">
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-amber-400 font-medium mb-2">
            {text.penalty}
          </p>
          <p className="text-sm text-gray-400">
            Thousands of traders use automated solutions to stay compliant and avoid costly audits.
          </p>
          <p className="text-sm text-green-400 mt-2 flex items-center gap-1.5">
            <Shield size={14} />
            {text.compliance}
          </p>
        </div>
      </div>
    </div>
  );
};

// Email Capture Component
export const TaxReportEmailCapture = ({ region = 'usa' }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const regionLabels = {
    usa: 'US',
    uk: 'UK',
    europe: 'EU',
    bitcoin: 'your country'
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      console.log('Tax report email captured:', email, { region });
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="my-8 p-5 rounded-xl bg-green-500/10 border border-green-500/30 text-center">
        <CheckCircle2 size={24} className="mx-auto text-green-400 mb-2" />
        <p className="text-green-400 font-medium">Report saved! Check your email for updates.</p>
      </div>
    );
  }

  return (
    <div className="my-8 p-5 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/5 border border-blue-500/30">
      <div className="flex items-center justify-center gap-2 mb-2">
        <Mail size={18} className="text-blue-400" />
        <h3 className="text-lg font-semibold text-white">Get Your Crypto Tax Report</h3>
      </div>
      
      <p className="text-sm text-gray-400 text-center mb-4">
        Save your calculation and receive {regionLabels[region]} tax updates
      </p>
      
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          className="flex-1 px-4 py-2.5 text-sm bg-gray-900/60 border border-gray-700/50 rounded-lg focus:border-blue-500/50 focus:outline-none focus:ring-1 focus:ring-blue-500/30 text-white placeholder-gray-500"
          data-testid="tax-email-input"
        />
        <button
          type="submit"
          className="px-5 py-2.5 text-sm font-medium bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/40 hover:border-blue-500/60 rounded-lg text-blue-400 transition-all flex items-center justify-center gap-2"
          data-testid="tax-email-submit"
        >
          <Bell size={14} />
          Send Report
        </button>
      </form>
    </div>
  );
};

// Combined Monetization Section
export const TaxPageMonetization = ({ 
  taxOwed = null, 
  region = 'usa',
  complianceText = ''
}) => {
  return (
    <>
      {/* CTA After Results */}
      <TaxOptimizerCTA 
        taxOwed={taxOwed} 
        region={region}
        complianceText={complianceText}
      />
      
      {/* Comparison Table */}
      <TaxSoftwareTable region={region} />
      
      {/* Trust & Urgency */}
      <TrustUrgencyBlock region={region} />
    </>
  );
};

export default TaxPageMonetization;
